import {
  Injectable,
  Inject,
  InjectionToken,
  Optional,
  RendererFactory2,
  DOCUMENT
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { SvgSanitizer } from '../utils/sanitization/svg-sanitizer';

import type { OnDestroy, Renderer2 } from '@angular/core';

export const THEMES = new InjectionToken<Array<Theme>>('themes');

export interface Theme {
  name: string;
  themeClass: string;
  spriteUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private theme: Theme;
  private renderer: Renderer2;
  private currentSprite: unknown;

  readonly onThemeChange: BehaviorSubject<Theme>;

  constructor(@Inject(DOCUMENT) private document: Document,
              @Inject(RendererFactory2) rendererFactory: RendererFactory2,
              @Inject(SvgSanitizer) private svgSanitizer: SvgSanitizer,
              @Inject(HttpClient) private httpClient: HttpClient,
              @Optional() @Inject(THEMES) private readonly themes?: Array<Theme>) {
    this.onThemeChange = new BehaviorSubject<Theme>(this.theme);
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    this.onThemeChange.unsubscribe();
  }

  setTheme(themeName: string): Promise<void> {
    const theme = this.getTheme(themeName);
    if (theme !== undefined ) {
      // TODO: all browsers excepts IE can use external sprite references
      if (theme.spriteUrl !== undefined && theme.spriteUrl !== null && theme.spriteUrl.trim() !== '' ) {
        return lastValueFrom(this.httpClient.get(theme.spriteUrl,
          { responseType: 'text' }))
          .then((spriteData: string): void => {
            this.appendSprite(spriteData);
            this.setThemeClass(theme);
            this.theme = theme;
            this.onThemeChange.next(this.theme);
          });
      } else {
        this.setThemeClass(theme);
        this.theme = theme;
        this.onThemeChange.next(this.theme);
        return Promise.resolve();
      }
    }
    return Promise.reject(new Error(`Theme "${themeName}" is not found`));
  }

  getThemeName(): string {
    return this.theme.name;
  }

  private appendSprite(spriteData: string): void {
    if (this.currentSprite ) {
      this.renderer.removeChild(this.document.body, this.currentSprite);
    }
    this.currentSprite = this.createSpriteElement(spriteData);
    this.renderer.appendChild(this.document.body, this.currentSprite);
  }

  private createSpriteElement(spriteData: string): unknown {
    const spriteElement = this.renderer.createElement('div');
    this.renderer.setAttribute(spriteElement, 'style', 'display:none');
    const safeSpriteData = this.svgSanitizer.sanitize(this.document, spriteData);
    this.renderer.setProperty(spriteElement, 'innerHTML', safeSpriteData);
    return spriteElement;
  }

  private setThemeClass(theme: Theme): void {
    if (this.theme !== undefined) {
      this.renderer.removeClass(this.document.body, this.theme.themeClass);
    }
    this.renderer.addClass(this.document.body, theme.themeClass);
  }

  private getTheme(themeName: string): Theme {
    if (this.themes !== null) {
      return this.themes.find((theme: Theme): boolean => themeName === theme.name );
    }
  }
}
